import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Warehouse } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { ActiveAntsWarehouseCheckIn } from "src/types";

interface ActiveAntsWarehouseCheckInDetailsSectionProps {
  activeAntsWarehouseCheckIn: ActiveAntsWarehouseCheckIn;
}

export const ActiveAntsWarehouseCheckInDetailsSection: React.FC<ActiveAntsWarehouseCheckInDetailsSectionProps> = ({
  activeAntsWarehouseCheckIn
}) => {
  const isPurchaseOrderCheckIn = activeAntsWarehouseCheckIn.type === "PURCHASE_ORDER";
  const hasReceivingDiscrepancy =
    isPurchaseOrderCheckIn &&
    (activeAntsWarehouseCheckIn.status === "Flagged" || activeAntsWarehouseCheckIn.status === "Resolved");

  const hasContent =
    activeAntsWarehouseCheckIn.inboundId !== undefined ||
    !!activeAntsWarehouseCheckIn.inboundReference ||
    activeAntsWarehouseCheckIn.inboundPackingSlipId !== undefined ||
    !!activeAntsWarehouseCheckIn.inboundStatus ||
    !!activeAntsWarehouseCheckIn.receivedIn ||
    !!activeAntsWarehouseCheckIn.completedOn ||
    isPurchaseOrderCheckIn;

  if (!hasContent) return null;

  return (
    <section className="rounded-2xl border border-secondary-200 bg-white shadow-soft">
      <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
        <Warehouse className="h-5 w-5 text-primary-600" />
        <h3 className="text-base font-semibold text-secondary-900">Active Ants Details</h3>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {activeAntsWarehouseCheckIn.inboundId !== undefined && (
            <div>
              <span className="text-neutral-500">Inbound Event ID:</span>{" "}
              <span className="font-medium">{activeAntsWarehouseCheckIn.inboundId}</span>
            </div>
          )}
          {activeAntsWarehouseCheckIn.inboundReference && (
            <div>
              <span className="text-neutral-500">Inbound Reference:</span>{" "}
              <span className="font-medium">{activeAntsWarehouseCheckIn.inboundReference}</span>
            </div>
          )}
          {activeAntsWarehouseCheckIn.inboundPackingSlipId !== undefined && (
            <div>
              <span className="text-neutral-500">Inbound Packing Slip ID:</span>{" "}
              <span className="font-medium">{activeAntsWarehouseCheckIn.inboundPackingSlipId}</span>
            </div>
          )}
          {activeAntsWarehouseCheckIn.inboundStatus && (
            <div>
              <span className="text-neutral-500">Active Ants Status:</span>{" "}
              <span className="font-medium">{activeAntsWarehouseCheckIn.inboundStatus}</span>
            </div>
          )}
          {activeAntsWarehouseCheckIn.receivedIn && (
            <div>
              <span className="text-neutral-500">Received In:</span>{" "}
              <span className="font-medium">{activeAntsWarehouseCheckIn.receivedIn}</span>
            </div>
          )}
          {activeAntsWarehouseCheckIn.completedOn && (
            <div>
              <span className="text-neutral-500">Completed On:</span>{" "}
              <span className="font-medium">{new Date(activeAntsWarehouseCheckIn.completedOn).toLocaleString()}</span>
            </div>
          )}
        </div>

        {(isPurchaseOrderCheckIn || hasReceivingDiscrepancy) && (
          <div className="mt-2 flex flex-wrap gap-3 pt-2 border-t border-secondary-100">
            {isPurchaseOrderCheckIn && (
              <Link
                to={`${ROUTES_CONFIG.activeAntsPurchaseOrder.path}?id=${activeAntsWarehouseCheckIn.id}`}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" /> View Active Ants PO
              </Link>
            )}
            {hasReceivingDiscrepancy && (
              <Link
                to={`${ROUTES_CONFIG.activeAntsReceivingDiscrepancies.path}?id=${activeAntsWarehouseCheckIn.id}`}
                className="inline-flex items-center gap-1 text-sm text-red-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" /> View Receiving Discrepancy
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
